import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { communityApi, houseApi } from './endpoints';

export function useHouse() {
  return useQuery({ queryKey: ['house'], queryFn: () => houseApi.getCurrent() });
}

export function useUpdateHouse() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: unknown) => houseApi.updateCurrent(data),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['house'] });
    },
  });
}

export function useHouseSettings() {
  return useQuery({ queryKey: ['house', 'settings'], queryFn: () => houseApi.getSettings() });
}

export function usePeople(params?: Record<string, unknown>) {
  return useQuery({ queryKey: ['people', params], queryFn: () => communityApi.listPeople(params) });
}

export function usePerson(id: string) {
  return useQuery({
    queryKey: ['people', id],
    queryFn: () => communityApi.getPerson(id),
    enabled: !!id,
  });
}

export function useCreatePerson() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: unknown) => communityApi.createPerson(data),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['people'] });
    },
  });
}

/** Queries that embed person names / assignments — must refresh after person edits. */
function invalidatePersonDependentCaches(qc: ReturnType<typeof useQueryClient>) {
  void qc.invalidateQueries({ queryKey: ['people'] });
  void qc.invalidateQueries({ queryKey: ['tasks'] });
  void qc.invalidateQueries({ queryKey: ['obligations'] });
  void qc.invalidateQueries({ queryKey: ['roles'] });
  void qc.invalidateQueries({ queryKey: ['functions'] });
  void qc.invalidateQueries({ queryKey: ['finance'] });
  void qc.invalidateQueries({ queryKey: ['communication'] });
  void qc.invalidateQueries({ queryKey: ['events'] });
  void qc.invalidateQueries({ queryKey: ['reports'] });
}

export function useUpdatePerson() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: unknown }) =>
      communityApi.updatePerson(id, data),
    onSuccess: () => {
      invalidatePersonDependentCaches(qc);
    },
  });
}

export function useChangePersonStatus() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: unknown }) =>
      communityApi.changePersonStatus(id, data),
    onSuccess: () => {
      invalidatePersonDependentCaches(qc);
    },
  });
}

export function useDirectory(params?: Record<string, unknown>) {
  return useQuery({
    queryKey: ['people', 'directory', params],
    queryFn: () => communityApi.directory(params),
  });
}

export function usePersonAccess(id: string, enabled = true) {
  return useQuery({
    queryKey: ['people', id, 'access'],
    queryFn: () => communityApi.getAccess(id),
    enabled: !!id && enabled,
  });
}

function useAccessMutation<T>(fn: (id: string) => Promise<T>) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: fn,
    onSuccess: (_data, id) => {
      void qc.invalidateQueries({ queryKey: ['people', id] });
      void qc.invalidateQueries({ queryKey: ['people'] });
      void qc.invalidateQueries({ queryKey: ['roles'] });
    },
  });
}

export function useGrantOperatorAccess() {
  return useAccessMutation((id) => communityApi.grantOperator(id));
}

export function useRevokeOperatorAccess() {
  return useAccessMutation((id) => communityApi.revokeOperator(id));
}

export function useResendOperatorInvitation() {
  return useAccessMutation((id) => communityApi.resendOperatorInvitation(id));
}

export function useCancelOperatorInvitation() {
  return useAccessMutation((id) => communityApi.cancelOperatorInvitation(id));
}
